import { TFunction } from 'i18next';
import { WidgetsKindEnum } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import WidgetFieldNames from '@lib/enums/fieldNames/widgetFieldNames';
import getTimeZones from '@lib/utils/getTimeZones';
import getColorOptions from '@lib/components/Chip/utils/colorEnum';
import { LocalWidget } from 'routes/Home/hooks/useWidgetEditor';

interface LocationValue {
  name: string;
  lat: number;
  lon: number;
}

interface ButtonItemValue {
  enabled?: boolean | null;
  updatedAt?: string | null;
}

interface ButtonsValue {
  task?: ButtonItemValue | null;
  intervention?: ButtonItemValue | null;
  formTemplate?: ButtonItemValue | null;
  asset?: ButtonItemValue | null;
  site?: ButtonItemValue | null;
  teamMember?: ButtonItemValue | null;
}

/**
 * Maps widget kind to icon name
 */
function getWidgetIcon(kind: string): string {
  const iconMap: Record<string, string> = {
    [WidgetsKindEnum.ActiveInterventions]: 'build',
    [WidgetsKindEnum.Announcement]: 'campaign',
    [WidgetsKindEnum.Clock]: 'schedule',
    [WidgetsKindEnum.ExternalLink]: 'link',
    [WidgetsKindEnum.QuickCreation]: 'add',
    [WidgetsKindEnum.SectorsPerformanceMetricsByDeadlinesMissed]:
      'clock_loader_20',
    [WidgetsKindEnum.SitesPerformanceMetricsByDeadlinesMissed]:
      'clock_loader_20',
    [WidgetsKindEnum.SitesMap]: 'location_on',
    [WidgetsKindEnum.WeatherToday]: 'wb_sunny',
    [WidgetsKindEnum.WeatherWeek]: 'wb_sunny',
  };

  return iconMap[kind] || 'widgets';
}

/**
 * Maps widget kind to display label
 */
function getWidgetLabel(kind: string): string {
  const labelMap: Record<string, string> = {
    [WidgetsKindEnum.ActiveInterventions]: 'Active Interventions',
    [WidgetsKindEnum.Announcement]: 'Announcement',
    [WidgetsKindEnum.Clock]: 'Clock',
    [WidgetsKindEnum.ExternalLink]: 'External Link',
    [WidgetsKindEnum.QuickCreation]: 'Quick Creation',
    [WidgetsKindEnum.SectorsPerformanceMetricsByDeadlinesMissed]:
      'Sectors Performance',
    [WidgetsKindEnum.SitesPerformanceMetricsByDeadlinesMissed]:
      'Sites Performance',
    [WidgetsKindEnum.SitesMap]: 'Sites Map',
    [WidgetsKindEnum.WeatherToday]: 'Weather Today',
    [WidgetsKindEnum.WeatherWeek]: 'Weather Week',
  };

  return labelMap[kind] || kind;
}

/**
 * Converts a LocalWidget to form default values for editing
 */
export default function getEditWidgetDefaultValues(
  widget: LocalWidget,
  t: TFunction<'translation', undefined>,
): Values {
  // If widget has formValues (was added/edited in current session), use them directly
  // This preserves the form state for widgets that haven't been saved to API yet
  if (widget.formValues) {
    return widget.formValues;
  }

  const values: Values = {};

  // Build the widget option for the select field
  const widgetOption = {
    value: widget.widgetTemplateId || '',
    label: getWidgetLabel(widget.kind),
    data: {
      id: widget.widgetTemplateId || '',
      kind: widget.kind,
      icon: getWidgetIcon(widget.kind),
      description: '',
    },
  };

  values[WidgetFieldNames.Widget] = widgetOption;

  const kind = widget.kind as WidgetsKindEnum;
  const fields = widget.fields || {};

  switch (kind) {
    case WidgetsKindEnum.ActiveInterventions: {
      values[WidgetFieldNames.ShowOnlyMyInterventions] = Boolean(fields.onlyMy);
      break;
    }

    case WidgetsKindEnum.Announcement: {
      // Title
      values[WidgetFieldNames.AnnouncementTitle] = widget.title || '';

      // Message content (stored in description)
      values[WidgetFieldNames.AnnouncementMessageContent] =
        widget.description || '';

      // Background color
      const backgroundColor = fields.backgroundColor as string | undefined;
      if (backgroundColor) {
        const colorOptions = getColorOptions({ t });
        const colorOption = colorOptions.find(
          (opt) => opt.value === backgroundColor,
        );
        values[WidgetFieldNames.AnnouncementBoxColor] = colorOption || null;
      }

      // Schedule dates
      const startAt = fields.startAt as string | undefined;
      const endAt = fields.endAt as string | undefined;
      if (startAt) {
        values[WidgetFieldNames.AnnouncementStartAt] = startAt;
      }
      if (endAt) {
        values[WidgetFieldNames.AnnouncementEndAt] = endAt;
      }

      // Image attachment
      if (widget.attachmentsAttached && widget.attachmentsAttached.length > 0) {
        const [firstAttachment] = widget.attachmentsAttached;
        values[WidgetFieldNames.AnnouncementImage] = firstAttachment;
      }
      break;
    }

    case WidgetsKindEnum.Clock: {
      const timezone = fields.timezone as string | undefined;
      if (timezone) {
        const timezoneOptions = getTimeZones();
        const timezoneOption = timezoneOptions.find(
          (opt) => opt.value === timezone,
        );
        values[WidgetFieldNames.Timezone] = timezoneOption || null;
      }
      break;
    }

    case WidgetsKindEnum.ExternalLink: {
      values[WidgetFieldNames.ExternalUrl] = (fields.link as string) || '';
      values[WidgetFieldNames.ExternalLinkTitle] = widget.title || '';
      values[WidgetFieldNames.ExternalLinkDescription] =
        widget.description || '';
      break;
    }

    case WidgetsKindEnum.WeatherToday: {
      const locationStr = fields.location as string | undefined;
      if (locationStr) {
        try {
          const location = JSON.parse(locationStr) as LocationValue;
          values[WidgetFieldNames.WeatherTodayLocation] = location;
        } catch {
          // Invalid JSON, ignore
        }
      }
      break;
    }

    case WidgetsKindEnum.WeatherWeek: {
      const locationStr = fields.location as string | undefined;
      if (locationStr) {
        try {
          const location = JSON.parse(locationStr) as LocationValue;
          values[WidgetFieldNames.WeatherWeekLocation] = location;
        } catch {
          // Invalid JSON, ignore
        }
      }
      break;
    }

    case WidgetsKindEnum.QuickCreation: {
      const buttons = fields.buttons as ButtonsValue | undefined;
      if (buttons) {
        values[WidgetFieldNames.QuickCreationTask] = Boolean(
          buttons.task?.enabled,
        );
        values[WidgetFieldNames.QuickCreationIntervention] = Boolean(
          buttons.intervention?.enabled,
        );
        values[WidgetFieldNames.QuickCreationFormTemplate] = Boolean(
          buttons.formTemplate?.enabled,
        );
        values[WidgetFieldNames.QuickCreationEquipment] = Boolean(
          buttons.asset?.enabled,
        );
        values[WidgetFieldNames.QuickCreationSite] = Boolean(
          buttons.site?.enabled,
        );
        values[WidgetFieldNames.QuickCreationTeamMember] = Boolean(
          buttons.teamMember?.enabled,
        );
      }
      break;
    }

    default:
      break;
  }

  return values;
}
